import React, { useCallback, useEffect, useState } from 'react'
import { Alert, ScrollView, TouchableOpacity, View } from 'react-native'
import { useSelector, useDispatch } from 'react-redux'
import { useSafeAreaInsets } from 'react-native-safe-area-context'
import { useNavigation } from '@react-navigation/native'
import { NavigationContainer } from '@screens/Common/containers'
import { Icon, Text, Toggle } from '@components/base'
import {
  chatbotQuickPromptsSelector,
  chatbotSettingsSelector,
} from '@screens/Nora/models/nora.selectors'
import { StyleService, useStyleSheet } from '@style/service'
import { ChatbotQuickPrompt } from '@src/types'
import { useLazyScreenPreload } from '@navigation/utils'
import { useIsDarkMode } from '@config/theme'
const MAX_VISIBLE_PROMPTS = 4

const GENERAL_ITEMS = [
  {
    icon: 'bell',
    title: 'Notifications',
    subtitle: 'Manage alerts and insights',
    screen: 'NoraNotificationSettingsScreen' as const,
  },
  {
    icon: 'chat',
    title: 'Chat Preferences',
    subtitle: 'Response style and length',
    screen: 'NoraChatPreferencesSettingsScreen' as const,
  },
  {
    icon: 'question',
    title: 'Help & Support',
    subtitle: 'FAQ, contact, feedback',
    screen: 'SupportHub' as const,
  },
] as const

export const NoraSettingsScreen = () => {
  const styles = useStyleSheet(themedStyle)
  const insets = useSafeAreaInsets()
  const navigation = useNavigation()
  const dispatch = useDispatch()
  const isDarkMode = useIsDarkMode()
  const quickPrompts = useSelector(chatbotQuickPromptsSelector)
  const settings = useSelector(chatbotSettingsSelector)
  const [dataSharingEnabled, setDataSharingEnabled] = useState(
    settings?.dataSharingEnabled ?? false,
  )

  useEffect(() => {
    dispatch({ type: 'nora/chatbotSettings' })
  }, [dispatch])

  const latestDataSharing = settings?.dataSharingEnabled ?? false
  useEffect(() => {
    setDataSharingEnabled(latestDataSharing)
  }, [latestDataSharing])

  const handleDataSharingToggle = useCallback(() => {
    if (dataSharingEnabled) {
      Alert.alert(
        'Turn off health data sharing?',
        'If you turn this off, Nora may give less accurate insights and recommendations.',
        [
          { text: 'Keep Sharing', style: 'cancel' },
          {
            text: 'Turn Off',
            style: 'destructive',
            onPress: () => {
              setDataSharingEnabled(false)
              dispatch({
                type: 'nora/updateChatbotSettings',
                payload: { dataSharingEnabled: false },
              })
            },
          },
        ],
      )
      return
    }

    setDataSharingEnabled(true)
    dispatch({ type: 'nora/updateChatbotSettings', payload: { dataSharingEnabled: true } })
  }, [dataSharingEnabled, dispatch])

  useLazyScreenPreload('UpsertQuickPromptModal')
  useLazyScreenPreload('NoraChatPreferencesSettingsScreen')
  useLazyScreenPreload('NoraNotificationSettingsScreen')
  useLazyScreenPreload('SupportHub')

  const visiblePrompts = quickPrompts.slice(0, MAX_VISIBLE_PROMPTS)

  const handleAddPrompt = () => {
    navigation.navigate('UpsertQuickPromptModal')
  }

  const handleEditPrompt = (prompt: ChatbotQuickPrompt) => {
    navigation.navigate('UpsertQuickPromptModal', {
      id: prompt.id,
      title: prompt.title,
      text: prompt.text,
    })
  }

  const handleDeletePrompt = useCallback(
    (prompt: ChatbotQuickPrompt) => {
      dispatch({ type: 'nora/deleteChatbotQuickPrompt', payload: { id: prompt.id } })
    },
    [dispatch],
  )

  return (
    <NavigationContainer
      title="Settings"
      leftIcon={<Icon name="arrow-left" size="24" weight="bold" style={styles.leftIcon} />}
      style={styles.screenBackground}
      navigationBarProps={styles.screenBackground}
    >
      <View style={styles.divider} />
      <ScrollView
        style={styles.container}
        contentContainerStyle={{ paddingBottom: insets.bottom + 16 }}
      >
        <View style={styles.sectionHeader}>
          <Text type="regular" bold style={styles.sectionTitle}>
            QUICK PROMPTS
          </Text>
          <TouchableOpacity
            style={styles.addButton}
            accessibilityLabel="Add quick prompt"
            onPress={handleAddPrompt}
          >
            <Icon name="plus" size="16" weight="bold" style={styles.addIcon} />
            <Text type="regular" bold style={styles.addText}>
              Add
            </Text>
          </TouchableOpacity>
        </View>
        {visiblePrompts.length > 0 && (
          <View style={[styles.sectionGroup, isDarkMode && styles.sectionGroupDark]}>
            <View style={styles.sectionGroupInner}>
              {visiblePrompts.map((prompt, index) => (
                <View key={prompt.id}>
                  {index > 0 && <View style={styles.divider} />}
                  <View style={styles.promptRow}>
                    <View style={styles.promptContent}>
                      <Text type="regular" numberOfLines={1}>
                        {prompt.title}
                      </Text>
                      <Text type="small" style={styles.promptText} numberOfLines={1}>
                        {prompt.text}
                      </Text>
                    </View>
                    <View style={styles.promptActions}>
                      {prompt.custom && (
                        <TouchableOpacity
                          accessibilityLabel={`Edit ${prompt.title}`}
                          onPress={() => handleEditPrompt(prompt)}
                        >
                          <Icon name="pencil-simple" size="20" style={styles.actionIcon} />
                        </TouchableOpacity>
                      )}
                      <TouchableOpacity
                        accessibilityLabel={`Delete ${prompt.title}`}
                        onPress={() => handleDeletePrompt(prompt)}
                      >
                        <Icon name="trash" size="20" style={styles.actionIcon} />
                      </TouchableOpacity>
                    </View>
                  </View>
                </View>
              ))}
            </View>
          </View>
        )}
        <View style={styles.sectionHeader}>
          <Text type="regular" bold style={styles.sectionTitle}>
            GENERAL
          </Text>
        </View>
        <View style={[styles.sectionGroup, isDarkMode && styles.sectionGroupDark]}>
          <View style={styles.sectionGroupInner}>
            {GENERAL_ITEMS.map((item, index) => (
              <View key={item.title}>
                {index > 0 && <View style={styles.divider} />}
                <TouchableOpacity
                  style={styles.generalRow}
                  accessibilityLabel={item.title}
                  onPress={() => navigation.navigate(item.screen)}
                >
                  <View style={styles.generalIcon}>
                    <Icon name={item.icon} size="20" />
                  </View>
                  <View style={styles.generalContent}>
                    <Text type="regular" numberOfLines={1}>
                      {item.title}
                    </Text>
                    <Text type="small" style={styles.promptText} numberOfLines={2}>
                      {item.subtitle}
                    </Text>
                  </View>
                  <Icon name="caret-right" size="16" style={styles.actionIcon} />
                </TouchableOpacity>
              </View>
            ))}
            <View style={styles.divider} />
            <View style={styles.generalRow}>
              <View style={styles.generalIcon}>
                <Icon name="shield-check" size="20" />
              </View>
              <View style={styles.generalContent}>
                <Text type="regular" numberOfLines={1}>
                  Share Health Data with Nora
                </Text>
                <Text type="small" style={styles.promptText} numberOfLines={2}>
                  Allow Nora to analyze your glucose, meals and activity
                </Text>
              </View>
              {/* TouchableOpacity intercepts taps when disabling to show confirmation alert
                 before the Toggle animates, preventing a visual flip-and-revert */}
              <TouchableOpacity
                accessibilityLabel="Toggle health data sharing"
                activeOpacity={1}
                onPress={dataSharingEnabled ? handleDataSharingToggle : undefined}
              >
                <View pointerEvents={dataSharingEnabled ? 'none' : 'auto'}>
                  <Toggle
                    style={styles.toggle}
                    checked={dataSharingEnabled}
                    onChange={dataSharingEnabled ? undefined : handleDataSharingToggle}
                  />
                </View>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </ScrollView>
    </NavigationContainer>
  )
}

const themedStyle = StyleService.create({
  screenBackground: {
    backgroundColor: 'theme.surface.base3',
  },
  container: {
    flex: 1,
  },
  leftIcon: {
    marginLeft: 16,
  },
  sectionHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 16,
    paddingTop: 24,
    paddingBottom: 8,
  },
  sectionTitle: {
    color: 'theme.text.tertiary',
  },
  addButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  addIcon: {
    tintColor: 'theme.text.link',
  },
  addText: {
    color: 'theme.text.link',
  },
  sectionGroup: {
    marginHorizontal: 16,
    backgroundColor: 'theme.background',
    borderRadius: 16,
    shadowColor: 'theme.solid.black',
    shadowOffset: { width: 0, height: 0 },
    shadowOpacity: 0.1,
    shadowRadius: 9,
    elevation: 4,
  },
  sectionGroupDark: {
    backgroundColor: 'theme.surface.base1',
    shadowColor: 'theme.solid.white',
    shadowOpacity: 0.05,
  },
  sectionGroupInner: {
    borderRadius: 16,
    overflow: 'hidden',
  },
  promptRow: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 16,
    gap: 12,
  },
  promptContent: {
    flex: 1,
    gap: 4,
  },
  promptText: {
    color: 'theme.text.secondary',
  },
  promptActions: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 16,
  },
  actionIcon: {
    tintColor: 'theme.text.secondary',
  },
  generalRow: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 16,
    gap: 12,
  },
  generalIcon: {
    width: 36,
    height: 36,
    borderRadius: 18,
    backgroundColor: 'theme.surface.base2',
    alignItems: 'center',
    justifyContent: 'center',
  },
  generalContent: {
    flex: 1,
    gap: 4,
  },
  divider: {
    height: 1,
    backgroundColor: 'theme.surface.base2',
  },
  toggle: {
    transform: [{ scale: 0.8 }],
  },
})
